/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useState, useEffect, useRef} from "react";
import { goBack } from "connected-react-router";
import { Prompt,useLocation, useHistory } from "react-router";

// material;
import {Tip, history, CheckboxField, SelectableList, Group} from '@insticator/insticator-ui';
import SectionGroup from "../../../../material/expandablesection/sectiongroup/sectiongroup";
import ExpandableSection from "../../../../material/expandablesection/expandablesection";
import SortableListSection from "../../../../material/sortablelistsection/sortablelistsection";

// components
import DimensionSection from "../../monetization/dimensionsandmetrics/dimensionsandmetrics";
import GeneralSettings from './generalsettings/generalsettings';
import ConfigController from "../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import ConfigV3Controller from "./material/configv3controller/configv3controller";
import Checklist from "../../monetization/checklist/checklist";
import Deliverysettings from "./deliverysettings/deliverysettings";
import Formatsettings from "./formatsettings/formatsettings";

// utils
import { validate, formDiff } from "./utils/validateform";
import {getSavePayload} from "../reportpreview/utils/reportpayload";

// redux
import {toggleIsOpened, initReportForm, updateReportForm, setIsPreviewing, setPreviewVisited} from "../../../../state/slices/report";
import {downloadReport, createReport, updateReport} from "../../../../state/thunks/report";

// styles
import injectSheet from 'react-jss';
import styles from './monetizationformv2_styles';
import {useDispatch, useSelector} from "react-redux";

//icons
import { ReactComponent as DataUsage } from "assets/icons/dataUsage.svg";

// statics
import {
    METRICS_OPTIONS,
    DIMENSIONS,
    ACCOUNT_UUID,
    FILTERS,
    SITES,
    COUNTRIES,
    DEVICES,
    REPORT_UUID,
    REPORT_NAME,
    REPORT_RANGE,
    REPORT_FROM,
    REPORT_TO,
    NO_PAGEVIEW_METRIC_SUPPORT,
    NO_PAGEVIEW_DIMENSION_SUPPORT,
    CANCEL_TITLE,
    SAVE_TITLE,
    METRICS,
    DIMENSIONS_OPTIONS,
    TOTAL_METRICS_OPTIONS,
    NEW_REPORT_UUID,
    DEFAULT_FORM_VALUES,
    DEFAULT_DIMENSION_STATE,
    DEFAULT_METRIC_STATE,
    IS_SCHEDULED,
    SCHEDULE,
    DEFAULT_SCHEDULE,
} from "./consts";
import {PRODUCT_TYPE, WIN_RATE, BID_RATE, SITE} from "../../monetization/consts";
import {date} from "@storybook/addon-knobs";
import Filters from "../../monetization/filters/filters";

const WARNING_TEXT = 'Your report was not saved. If you navigate to a new page, all your changes will be lost.'
const DIMENSIONS_TOOLTIP = 'Dimensions will constitute individual columns on your report. Turn dimensions on and off, and reorganize them to customize your report.'
const MONETIZATION_TOOLTIP = 'Metrics will constitute columns on the report that will appear after the dimensions breakdown.'
const METRIC_ERROR_MESSAGE_NON_SELECTED = 'A minimum of one metric is required.'
const METRIC_FORM_DESC = 'Choose the dimensions you want to include in your report. Drag and drop to set the column order of dimensions within the spreadsheet.'

/* ========== ~~~~~~~~~~ MONETIZATION FORM V2 ~~~~~~~~~~ ========== */
export default injectSheet(styles)((props) => {
    const {uuid, reportActionType, hoveredRow, handleNewFormCancel} = props;

    const formValues = useSelector((state) => state.report[uuid]);
    const allReports = useSelector((state) => state.report.allReports);
    const formValuesFromDB = allReports.find(r => r.reportUUID === uuid);
    const mocked = useSelector((state) => state.session.mocked)
    const accountUUID = useSelector((state) => state.session.accountUUID);
    const selectedCountries = useSelector((state) => state.app.filters.countries)
    const selectedSites = useSelector((state) => state.app.filters.sites)
    const selectedSitesByType = useSelector((state) => state.app.filters.sitesByType)
    const selectedDevices = useSelector((state) => state.app.filters.devices)
    const selectedDateRange = useSelector((state) => state.app.filters.dateRange)
    const downloadStatus = useSelector((state) => state.report.downloadStatus[uuid]);
    const reportNames = allReports.filter(r => r.reportUUID !== props.uuid).map(r => r[REPORT_NAME] && r[REPORT_NAME].toLowerCase());
    const isPreviewing = useSelector((state) => state.report.isPreviewing);
    const reportPreviewVisited = useSelector((state) => state.report.reportPreviewVisited);
    const sitesAndAppsUUIDs = useSelector((state) => state.account.sitesAndAppsUUIDs)
    const isOpened = formValues?.isOpened || false
    const isNewForm = uuid === NEW_REPORT_UUID
    const isHovered = hoveredRow === uuid

    const dispatch = useDispatch();
    const location = useLocation();
    const previousLocation = useRef(location);
    const [canSave, setCanSave] = useState(false);

    const latestReportDate = new Date()
    latestReportDate.setDate(latestReportDate.getDate() - 1)
    const firstDay = new Date(Date.UTC(latestReportDate.getFullYear(), latestReportDate.getMonth(), 1));

    useEffect(() => {
        if (uuid && !isNewForm) {
            dispatch(initReportForm({ uuid, sitesAndAppsUUIDs }))
        }
        if (isNewForm && !reportPreviewVisited) { // setting defaults
            handleOnChange({
                [ACCOUNT_UUID]: accountUUID,
                [FILTERS]: {
                    [COUNTRIES]: [],
                    [SITES]: selectedSites.length ? selectedSitesByType : ["ALL"],
                    [DEVICES]: selectedDevices
                },
                [REPORT_RANGE]: "CUSTOM",
                [REPORT_FROM]: selectedDateRange.from ,
                [REPORT_TO]: selectedDateRange.to ,
                [REPORT_NAME]:'',
                [IS_SCHEDULED]:true,
                [SCHEDULE]:{...DEFAULT_SCHEDULE},
                ...DEFAULT_DIMENSION_STATE,
                ...DEFAULT_METRIC_STATE
            })
        }
        else if(isNewForm && reportPreviewVisited) {
            dispatch(setPreviewVisited(false));
        }
    }, [uuid])

    useEffect(() => {
        if (validate(formValues, formValuesFromDB, reportNames)) setCanSave(true)
        else setCanSave(false)
    }, [formValues, reportNames])


    const dimensionOptions = () => {
        if(!mocked) {
            return DIMENSIONS_OPTIONS.filter(o => o.isInternal === false);
        }
        else return DIMENSIONS_OPTIONS;
    }

    const totalMetricsOptions = () => {
        if(!mocked) {
            return TOTAL_METRICS_OPTIONS.filter(o => o.isInternal === false);
        }
        else {
            return TOTAL_METRICS_OPTIONS;
        }
    }

    const handleToggle = () => {
        if (isNewForm) {
            handleNewFormCancel()
            dispatch(goBack())
        } else {
            dispatch(toggleIsOpened(uuid))
        }
    }

    const handleOnChange = (newValues) => {
            dispatch(updateReportForm({...formValues, ...newValues}))
    }

    const disableOptions = (field, options) => {
        return options.some(o => formValues[field].includes(o))
    }

    const handlePreview = async() => {
        if (!canSave  && formDiff(formValues, formValuesFromDB).length > 0) return
        await dispatch(setIsPreviewing(true));
        history.push(`/dashboard/reportbuilder/preview?reportUUID=${uuid}`)
    }

    const isDownloading = downloadStatus && downloadStatus !== 'idle';
    const handleDownload = () => {
        if(!isDownloading) dispatch(downloadReport(uuid))
    }

    const handleSave = () => {
        const payload = getSavePayload(formValues, accountUUID, sitesAndAppsUUIDs)
        if (isNewForm) {
            delete payload[REPORT_UUID]
            dispatch(createReport(payload))
        } else {
            dispatch(toggleIsOpened(uuid))
            dispatch(updateReport(payload))
        }
    }

    if (!formValues) return null;
    if (!isOpened && !isHovered) return null;

    return (
        <>
            <Prompt message={WARNING_TEXT} when={canSave && !isPreviewing} />
            <div className={props.classes.monetization} >
                <ExpandableSection
                    isOpened={isOpened}
                    handleCancel={handleToggle}
                    cancelTitle={CANCEL_TITLE}
                    saveTitle={SAVE_TITLE}
                    isChanged={canSave}
                    handlePreview={handlePreview}
                    handleSave={handleSave}
                    handleDownload={!isNewForm && handleDownload}
                    isDownloading={isDownloading}
                    hideFooter={true}
                >
                    <SectionGroup>
                        <ConfigV3Controller
                            handleToggle={handleToggle}
                            handleCancel={handleToggle}
                            handleSave={handleSave}
                            isOpened={isOpened}
                            title={reportActionType}
                            description={''}
                            cancelTitle={CANCEL_TITLE}
                            saveTitle={SAVE_TITLE}
                            isChanged={canSave}
                            handleDownload={!isNewForm && handleDownload}
                            isDownloading={isDownloading}
                            handlePreview={handlePreview}
                            disableSave={!canSave}
                            icon={<DataUsage/>}
                        />
                        <GeneralSettings uuid={uuid} />

                        <div className={props.classes.reportSection}>
                            <h3 className={props.classes.reportHeader}>Report Builder</h3>
                            <Filters uuid={uuid}></Filters>
                            <div className={props.classes.selectionSection}>
                                <DimensionSection uuid={uuid} dimensionOptions={dimensionOptions()} metricsOptions={totalMetricsOptions()}></DimensionSection>
                            </div>
                        </div>
                        <div className={props.classes.formatSettings}>
                            <Formatsettings uuid={uuid} />
                        </div>
                        <div className={props.classes.deliverySettingsSection}>
                            <h3 className={props.classes.deliverySettingsTitle}>Scheduling</h3>
                            <Deliverysettings uuid={uuid}/>
                        </div>


                    </SectionGroup>

                </ExpandableSection>
            </div>
        </>
    )
});